import { lazy } from "react";

const dataPreloadPromises = new Map();

const runDataPreloadOnce = (key, loader) => {
  if (typeof loader !== "function") return Promise.resolve();
  if (dataPreloadPromises.has(key)) return dataPreloadPromises.get(key);

  const request = Promise.resolve()
    .then(loader)
    .catch((error) => {
      dataPreloadPromises.delete(key);
      throw error;
    });

  dataPreloadPromises.set(key, request);
  return request;
};

const lazyWithPreload = (routeKey, importer) => {
  let modulePromise = null;

  const loadModule = () => {
    if (!modulePromise) modulePromise = importer();
    return modulePromise;
  };

  const Component = lazy(loadModule);

  Component.preloadChunk = loadModule;

  Component.preload = async (context = {}) => {
    const module = await loadModule();

    if (typeof module.preloadData === "function") {
      const dataKey = `${routeKey}::${context.cacheKey || routeKey}`;
      await runDataPreloadOnce(dataKey, () => module.preloadData(context));
    }

    return module;
  };

  return Component;
};

// Standard Quotes
export const StandardQuotes = lazyWithPreload(
  "/admin/standard-quotes",
  () => import("../pages/adminPages/StandardQuotes")
);

// Public customer quote page
export const StandardQuotePublicPage = lazyWithPreload(
  "/quote/:token",
  () => import("../pages/StandardQuotePublicPage")
);

// Authentication
export const Login = lazyWithPreload(
  "/login",
  () => import("../pages/usersPages/Login")
);

export const Register = lazyWithPreload(
  "/register",
  () => import("../pages/usersPages/Register")
);

// Main pages
export const Home = lazyWithPreload(
  "/",
  () => import("../pages/home/Home")
);

export const Categories = lazyWithPreload(
  "/admin/categories",
  () => import("../pages/adminPages/Categories")
);

export const Products = lazyWithPreload(
  "/admin/products",
  () => import("../pages/adminPages/Products")
);

// Admin pages
export const AdminOrders = lazyWithPreload(
  "/admin/orders",
  () => import("../pages/adminPages/AdminOrders")
);

export const AdminUsers = lazyWithPreload(
  "/admin/users",
  () => import("../pages/adminPages/AdminUsers")
);

export const AdminProfile = lazyWithPreload(
  "/admin/profile",
  () => import("../pages/adminPages/AdminProfile")
);

export const AdminSettings = lazyWithPreload(
  "/admin/settings",
  () => import("../pages/adminPages/AdminSettings")
);

export const AdminSpareParts = lazyWithPreload(
  "/admin/spare-parts",
  () => import("../pages/adminPages/SpareParts")
);

export const Attachments = lazyWithPreload(
  "/admin/attachments",
  () => import("../pages/adminPages/Attachments")
);

export const CompatibilityRules = lazyWithPreload(
  "/admin/compatibility-rules",
  () => import("../pages/adminPages/CompatibilityRules")
);

export const AdminPackageForm = lazyWithPreload(
  "/admin/packages/form",
  () => import("../pages/adminPages/AdminPackageForm")
);

export const AdminPackagesList = lazyWithPreload(
  "/admin/packages",
  () => import("../pages/adminPages/AdminPackagesList")
);

export const AdminAttInventory = lazyWithPreload(
  "/admin/inventory",
  () => import("../pages/adminPages/Inventory")
);

export const DealerApplicationPage = lazyWithPreload(
  "/admin/dealers-applications",
  () => import("../pages/adminPages/DealerApplicationPage")
);

export const DiscountCodesPage = lazyWithPreload(
  "/admin/discounts",
  () => import("../pages/adminPages/DiscountCodes")
);

export const AdminFinancePage = lazyWithPreload(
  "/admin/finance",
  () => import("../pages/adminPages/AdminFinanceDashboard")
);

export const AdminBuildLeads = lazyWithPreload(
  "/admin/leads",
  () => import("../pages/adminPages/AdminBuildMachineLeads")
);

export const QuoteManagement = lazyWithPreload(
  "/admin/request-quotes",
  () => import("../pages/adminPages/QuoteManagement")
);

// Freight admin pages
export const AdminFreightCustomers = lazyWithPreload(
  "/admin/freight/customers",
  () => import("../pages/adminPages/freight/customers/Customers")
);

export const AdminFreight = lazyWithPreload(
  "/admin/freight/companies",
  () => import("../pages/adminPages/freight/Companies")
);

export const AdminFreightJobs = lazyWithPreload(
  "/admin/freight/jobs",
  () => import("../pages/adminPages/freight/Jobs")
);

export const AdminFreightQuotes = lazyWithPreload(
  "/admin/freight/quotes",
  () => import("../pages/adminPages/freight/Quotes")
);

// User pages
export const UsersProducts = lazyWithPreload(
  "/machines",
  () => import("../pages/usersPages/UsersProducts")
);

export const ProductDetailPage = lazyWithPreload(
  "/:slug",
  () => import("../pages/usersPages/ProductDetailPage")
);

export const ProductConfigurator = lazyWithPreload(
  "/admin/product-configurator/:id",
  () => import("../pages/usersPages/ProductConfigurator")
);

export const ConfigureForklift = lazyWithPreload(
  "/configure-machine/:id",
  () => import("../pages/usersPages/ConfigureForklift")
);

export const PaymentOption = lazyWithPreload(
  "/payment-options",
  () => import("../pages/usersPages/PaymentOption")
);

export const Checkout = lazyWithPreload(
  "/checkout",
  () => import("../pages/usersPages/CheckOut")
);

export const ThankYou = lazyWithPreload(
  "/thank-you",
  () => import("../pages/usersPages/ThankYou")
);

export const UserProfile = lazyWithPreload(
  "/profile",
  () => import("../pages/usersPages/UserProfile")
);

export const TermsAndConditions = lazyWithPreload(
  "/terms-and-conditions",
  () => import("../pages/usersPages/TermsAndConditions")
);

export const ContactPage = lazyWithPreload(
  "/contact",
  () => import("../pages/usersPages/ContactPage")
);

export const PrivacyPolicy = lazyWithPreload(
  "/privacy-policy",
  () => import("../pages/usersPages/PrivacyPolicy")
);

export const AboutPage = lazyWithPreload(
  "/about",
  () => import("../pages/usersPages/AboutPage")
);

export const PackageDetail = lazyWithPreload(
  "/package/:slug",
  () => import("../pages/usersPages/PackageDetail")
);

export const PackagesPage = lazyWithPreload(
  "/packages",
  () => import("../pages/usersPages/PackagesPage")
);

export const MyOrders = lazyWithPreload(
  "/my-orders",
  () => import("../pages/usersPages/MyOrders")
);

export const OrderDetail = lazyWithPreload(
  "/my-orders/:orderId",
  () => import("../pages/usersPages/OrderDetail")
);

export const ContractDetails = lazyWithPreload(
  "/contracts/:id",
  () => import("../pages/usersPages/ContractDetails")
);

export const VideosPage = lazyWithPreload(
  "/videos",
  () => import("../pages/usersPages/VideosPage")
);

export const VideoDetailPage = lazyWithPreload(
  "/videos/:slug",
  () => import("../pages/usersPages/VideoDetailPage")
);

export const FinancePage = lazyWithPreload(
  "/finance",
  () => import("../pages/usersPages/FinancePage")
);

export const BecomeDealerPage = lazyWithPreload(
  "/become-a-dealer",
  () => import("../pages/usersPages/BecomeDealer")
);

export const DealerFormPage = lazyWithPreload(
  "/applying-dealer",
  () => import("../pages/usersPages/DealerFormPage")
);

export const BlogPage = lazyWithPreload(
  "/blogs",
  () => import("../pages/usersPages/Blogpage")
);

export const SparePartsPage = lazyWithPreload(
  "/spareparts",
  () => import("../pages/usersPages/SparePartsPage")
);

export const SparePartsDetailPage = lazyWithPreload(
  "/spareparts/:handle",
  () => import("../pages/usersPages/SparePartsDetailPage")
);

export const CollectionDetailPage = lazyWithPreload(
  "/collections/:handle",
  () => import("../pages/usersPages/CollectionDetailPage")
);

// Layouts
export const AdminLayoutLazy = lazyWithPreload(
  "/admin-layout",
  () => import("../components/layout/AdminLayout")
);

export const FreightLayout = lazyWithPreload(
  "/freight-layout",
  () => import("../components/freight/FreightLayout")
);

export const FreightDashboardLayout = lazyWithPreload(
  "/freight-dashboard-layout",
  () => import("../components/freight/FreightDashboardLayout")
);

// Freight pages
export const FreightHome = lazyWithPreload(
  "/freight",
  () => import("../pages/freight/FreightHome")
);

export const FreightProfile = lazyWithPreload(
  "/freight/profile",
  () => import("../pages/freight/FreightProfile")
);

export const FreightLogin = lazyWithPreload(
  "/freight/login",
  () => import("../pages/freight/Login")
);

export const FreightRegister = lazyWithPreload(
  "/freight/register",
  () => import("../pages/freight/Register")
);

export const FreightDashboard = lazyWithPreload(
  "/freight/dashboard",
  () => import("../pages/freight/dashboard/Dashboard")
);

export const FreightJobs = lazyWithPreload(
  "/freight/jobs",
  () => import("../pages/freight/jobs/Jobs")
);

export const FreightQuotes = lazyWithPreload(
  "/freight/quotes",
  () => import("../pages/freight/quotes/Quotes")
);

export const CustomerLogin = lazyWithPreload(
  "/freight/customer-login",
  () => import("../pages/freight/CustomerLogin")
);

export const CustomerRegister = lazyWithPreload(
  "/freight/customer-register",
  () => import("../pages/freight/CustomerRegister")
);

export const FreightTerms = lazyWithPreload(
  "/freight/terms",
  () => import("../pages/freight/Terms")
);

export const FreightPrivacy = lazyWithPreload(
  "/freight/privacy",
  () => import("../pages/freight/Privacy")
);

// Route resolver
const normalizePath = (path) => {
  if (!path) return "/";

  const cleanPath = path.split("?")[0].split("#")[0] || "/";

  return cleanPath.length > 1
    ? cleanPath.replace(/\/+$/, "")
    : cleanPath;
};

const exactRoutes = new Map([
  ["/", [Home]],
  ["/login", [Login]],
  ["/register", [Register]],
  ["/machines", [UsersProducts]],
  ["/find-my-machine", [UsersProducts]],
  ["/spareparts", [SparePartsPage]],
  ["/terms-and-conditions", [TermsAndConditions]],
  ["/contact", [ContactPage]],
  ["/blogs", [BlogPage]],
  ["/privacy-policy", [PrivacyPolicy]],
  ["/about", [AboutPage]],
  ["/packages", [PackagesPage]],
  ["/profile", [UserProfile]],
  ["/my-orders", [MyOrders]],
  ["/videos", [VideosPage]],
  ["/videos/:slug", [VideoDetailPage]],
  ["/finance", [FinancePage]],
  ["/become-a-dealer", [BecomeDealerPage]],
  ["/applying-dealer", [DealerFormPage]],
  ["/payment-options", [PaymentOption]],
  ["/checkout", [Checkout]],
  ["/thank-you", [ThankYou]],

  ["/admin", [AdminLayoutLazy, Products]],
  ["/admin/categories", [AdminLayoutLazy, Categories]],
  ["/admin/inventory", [AdminLayoutLazy, AdminAttInventory]],
  ["/admin/users", [AdminLayoutLazy, AdminUsers]],
  ["/admin/finance", [AdminLayoutLazy, AdminFinancePage]],
  ["/admin/leads", [AdminLayoutLazy, AdminBuildLeads]],
  ["/admin/products", [AdminLayoutLazy, Products]],
  ["/admin/spare-parts", [AdminLayoutLazy, AdminSpareParts]],
  ["/admin/attachments", [AdminLayoutLazy, Attachments]],
  ["/admin/compatibility-rules", [AdminLayoutLazy, CompatibilityRules]],
  ["/admin/orders", [AdminLayoutLazy, AdminOrders]],
  ["/admin/profile", [AdminLayoutLazy, AdminProfile]],
  ["/admin/settings", [AdminLayoutLazy, AdminSettings]],
  ["/admin/packages", [AdminLayoutLazy, AdminPackagesList]],
  ["/admin/packages/create", [AdminLayoutLazy, AdminPackageForm]],
  ["/admin/dealers-applications", [AdminLayoutLazy, DealerApplicationPage]],
  ["/admin/discounts", [AdminLayoutLazy, DiscountCodesPage]],
  ["/admin/freight/companies", [AdminLayoutLazy, AdminFreight]],
  ["/admin/freight/jobs", [AdminLayoutLazy, AdminFreightJobs]],
  ["/admin/freight/quotes", [AdminLayoutLazy, AdminFreightQuotes]],
  ["/admin/freight/customers", [AdminLayoutLazy, AdminFreightCustomers]],
  ["/admin/request-quotes", [AdminLayoutLazy, QuoteManagement]],

  // Standard Quotes: list and create page use the same component
  ["/admin/standard-quotes", [AdminLayoutLazy, StandardQuotes]],
  ["/admin/standard-quotes/new", [AdminLayoutLazy, StandardQuotes]],

  ["/quote/:token", [StandardQuotePublicPage]],

  ["/freight", [FreightLayout, FreightHome]],
  ["/freight/login", [FreightLayout, FreightLogin]],
  ["/freight/register", [FreightLayout, FreightRegister]],
  ["/freight/customer-login", [FreightLayout, CustomerLogin]],
  ["/freight/customer-register", [FreightLayout, CustomerRegister]],
  ["/freight/terms", [FreightLayout, FreightTerms]],
  ["/freight/privacy", [FreightLayout, FreightPrivacy]],
  ["/freight/dashboard", [FreightDashboardLayout, FreightDashboard]],
  ["/freight/profile", [FreightDashboardLayout, FreightProfile]],
  ["/freight/jobs", [FreightDashboardLayout, FreightJobs]],
  ["/freight/quotes", [FreightDashboardLayout, FreightQuotes]],
]);

const dynamicRoutes = [
  {
    match: (path) => path.match(/^\/collections\/([^/]+)$/),
    components: [CollectionDetailPage],
    params: (match) => ({ handle: match[1] }),
  },
  {
    match: (path) => path.match(/^\/spareparts\/([^/]+)$/),
    components: [SparePartsDetailPage],
    params: (match) => ({ handle: match[1] }),
  },
  {
    match: (path) => path.match(/^\/package\/([^/]+)$/),
    components: [PackageDetail],
    params: (match) => ({ slug: match[1] }),
  },
  {
    match: (path) => path.match(/^\/contracts\/([^/]+)$/),
    components: [ContractDetails],
    params: (match) => ({ id: match[1] }),
  },
  {
    match: (path) => path.match(/^\/my-orders\/([^/]+)$/),
    components: [OrderDetail],
    params: (match) => ({ orderId: match[1] }),
  },
  {
    match: (path) => path.match(/^\/configure-machine\/([^/]+)$/),
    components: [ConfigureForklift],
    params: (match) => ({ id: match[1] }),
  },
  {
    match: (path) =>
      path.match(/^\/admin\/product-configurator\/([^/]+)$/),
    components: [AdminLayoutLazy, ProductConfigurator],
    params: (match) => ({ id: match[1] }),
  },
  {
    match: (path) => path.match(/^\/quote\/([^/]+)$/),
    components: [StandardQuotePublicPage],
    params: (match) => ({ token: match[1] }),
  },

  // Standard Quote edit page
  {
    match: (path) =>
      path.match(/^\/admin\/standard-quotes\/([^/]+)\/edit$/),
    components: [AdminLayoutLazy, StandardQuotes],
    params: (match) => ({ id: match[1] }),
  },
  {
    match: (path) => path.match(/^\/admin\/packages\/edit\/([^/]+)$/),
    components: [AdminLayoutLazy, AdminPackageForm],
    params: (match) => ({ id: match[1] }),
  },
  {
    match: (path) => path.match(/^\/([^/]+)$/),
    components: [ProductDetailPage],
    params: (match) => ({ slug: match[1] }),
  },
];

const resolveRoute = (path) => {
  const normalizedPath = normalizePath(path);
  const exact = exactRoutes.get(normalizedPath);

  if (exact) {
    return {
      components: exact,
      context: {
        path: normalizedPath,
        params: {},
        cacheKey: normalizedPath,
      },
    };
  }

  for (const route of dynamicRoutes) {
    const match = route.match(normalizedPath);

    if (!match) continue;

    return {
      components: route.components,
      context: {
        path: normalizedPath,
        params: route.params ? route.params(match) : {},
        cacheKey: normalizedPath,
      },
    };
  }

  return {
    components: [],
    context: {
      path: normalizedPath,
      params: {},
      cacheKey: normalizedPath,
    },
  };
};

export const preloadRoute = (path) => {
  const { components, context } = resolveRoute(path);

  if (!components.length) return Promise.resolve([]);

  return Promise.allSettled(
    components.map((Component) =>
      typeof Component.preload === "function"
        ? Component.preload(context)
        : Promise.resolve()
    )
  );
};

export const preloadRouteChunk = (path) => {
  const { components } = resolveRoute(path);

  if (!components.length) return Promise.resolve([]);

  return Promise.allSettled(
    components.map((Component) =>
      typeof Component.preloadChunk === "function"
        ? Component.preloadChunk()
        : Promise.resolve()
    )
  );
};